import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    AlertCircle,
    FileText,
    ChevronDown,
    CalendarDays,
    Loader,
    Eye,
    CheckCircle2
} from "lucide-react";
import { fetchAllRequests, validateChangeRequest } from "../../redux/actions/changeRequestActions";
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../../api';
import "../adminrh/Traitement/TraitementStyles/TraitementConges.css"; // Réutilisation du style Congés

const fieldLabels = {
    firstName: "Prénom",
    lastName: "Nom",
    phone: "Téléphone",
    dateOfBirth: "Date de naissance",
    city: "Ville",
    bio: "Biographie",
    cin: "CIN",
    phoneUrgence: "Tél. Urgence",
    adresse: "Adresse",
    codePostal: "Code Postal",
    statutMarital: "Statut Marital",
    nombreEnfants: "Nombre d'enfants",
    personneUrgence: "Personne à prévenir",
    rib: "RIB",
    iban: "IBAN",
    profilePictureUrl: "Photo de profil",
    bannerPictureUrl: "Photo de couverture"
};

const ValidationModifProfil = () => {
    const dispatch = useDispatch();
    const { allRequests, loading } = useSelector(state => state.userChangeRequests || {});
    
    const [activeTab, setActiveTab] = useState("pending");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const { user } = useSelector(state => state.auth); // Récupération de l'user connecté

    const companyId = useMemo(() => {
        return user?.companyId || 
               sessionStorage.getItem('companyId') || 
               sessionStorage.getItem('company_id') || 
               1;
    }, [user]);

    useEffect(() => {
        console.log("DEBUG: Fetching requests for companyId:", companyId);
        dispatch(fetchAllRequests(companyId));
    }, [dispatch, companyId]);

    const handleApprove = (req) => {
        const validatorId = user?.id || sessionStorage.getItem('userId') || sessionStorage.getItem('uid');
        
        if (!validatorId) {
            Swal.fire('Erreur', 'Impossible d\'identifier le validateur. Veuillez vous reconnecter.', 'error');
            return;
        }

        Swal.fire({
            title: 'Approuver cette modification ?',
            text: `Le champ "${fieldLabels[req.fieldKey] || req.fieldKey}" sera mis à jour.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, approuver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(validateChangeRequest(req.id, 'approved', validatorId, null, companyId));
            }
        });
    };

    const handleReject = (req) => {
        const validatorId = user?.id || sessionStorage.getItem('userId') || sessionStorage.getItem('uid');
        
        if (!validatorId) {
            Swal.fire('Erreur', 'Impossible d\'identifier le validateur. Veuillez vous reconnecter.', 'error');
            return;
        }

        Swal.fire({
            title: 'Rejeter cette modification',
            input: 'textarea',
            inputPlaceholder: 'Motif du rejet...',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Rejeter',
            cancelButtonText: 'Annuler',
            inputValidator: (value) => {
                if (!value) return 'Le motif est obligatoire pour un rejet.';
            }
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(validateChangeRequest(req.id, 'rejected', validatorId, result.value, companyId));
            }
        });
    };

    const showImagePreview = (url, title) => {
        Swal.fire({
            title: title,
            imageUrl: url,
            imageAlt: title,
            width: 'auto',
            showCloseButton: true,
            showConfirmButton: false,
        });
    };

    const renderValue = (req, type) => {
        const value = type === 'new' ? req.newValue : req.oldValue;
        const isPhoto = req.fieldKey === 'profilePictureUrl' || req.fieldKey === 'bannerPictureUrl';
        
        if (!value) return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Vide</span>;

        if (isPhoto) {
            const photoType = req.fieldKey === 'profilePictureUrl' ? 'profile' : 'banner';
            const imageUrl = `${API_BASE_URL}/api/users/images/${photoType}/${value}`;
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => showImagePreview(imageUrl, fieldLabels[req.fieldKey])}>
                    <img src={imageUrl} alt="Preview" style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                    <Eye size={14} color="#2f478f" />
                </div>
            );
        }

        return <span style={{ fontWeight: '500', color: '#374151' }}>{value}</span>;
    };

    const filteredRequests = useMemo(() => {
        const list = Array.isArray(allRequests) ? allRequests : [];
        return list.filter(req => {
            const matchesTab = req.status === activeTab;
            const searchLower = searchTerm.toLowerCase();
            const fieldLabel = (fieldLabels[req.fieldKey] || req.fieldKey).toLowerCase();
            const userName = req.user ? `${req.user.firstName} ${req.user.lastName}`.toLowerCase() : '';
            
            const matchesSearch = 
                userName.includes(searchLower) ||
                fieldLabel.includes(searchLower) ||
                (req.newValue && req.newValue.toLowerCase().includes(searchLower));

            return matchesTab && matchesSearch;
        });
    }, [allRequests, activeTab, searchTerm]);

    const tabs = [
        { id: "pending", label: "En cours", count: (allRequests || []).filter(r => r.status === 'pending').length },
        { id: "approved", label: "Approuvées", count: (allRequests || []).filter(r => r.status === 'approved').length },
        { id: "rejected", label: "Refusées", count: (allRequests || []).filter(r => r.status === 'rejected').length },
    ];

    return (
        <div className="tcvPage">
            <div className="tcvWrap">
                {/* Header */}
                <div className="tcvHeader">
                    <div>
                        <h1>Validation des Modifications de Profil</h1>
                        <p>Gérez et validez les demandes de changement de données de vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Controls */}
                <div className="tcvCard">
                    <div className="tcvControls">
                        <div className="tcvSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par collaborateur, champ, valeur..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button type="button" className="tcvFilterBtn" onClick={() => setShowFilters(!showFilters)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tcvChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>
                </div>

                {/* Tabs & Table */}
                <div className="tcvCard tcvCard--noPad">
                    <div className="tcvTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tcvTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tcvCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="tcvTableWrap">
                        {loading && allRequests?.length === 0 ? (
                            <div className="tcvEmpty">
                                <Loader size={32} className="spin" />
                                <div>Chargement...</div>
                            </div>
                        ) : filteredRequests.length === 0 ? (
                            <div className="tcvEmpty">
                                <CheckCircle2 size={46} color="#94a3b8" />
                                <div>Aucune demande {activeTab === 'pending' ? 'en attente' : activeTab === 'approved' ? 'approuvée' : 'rejetée'}</div>
                            </div>
                        ) : (
                            <table className="tcvTable">
                                <thead>
                                    <tr>
                                        <th>Collaborateur</th>
                                        <th>Champ modifié</th>
                                        <th>Ancienne valeur</th>
                                        <th>Nouvelle valeur</th>
                                        {activeTab === 'pending' ? <th className="is-right">Actions</th> : <th>Traité le</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRequests.map((req) => (
                                        <tr key={req.id}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#ebf8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3182ce', fontWeight: 'bold', fontSize: '12px' }}>
                                                        {req.user?.firstName?.[0]}{req.user?.lastName?.[0]}
                                                    </div>
                                                    <div>
                                                        <div className="tcvPrimary">{req.user?.firstName} {req.user?.lastName}</div>
                                                        <div className="tcvSub">{req.user?.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ padding: '4px 10px', borderRadius: '12px', background: '#f1f5f9', fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                                                    {fieldLabels[req.fieldKey] || req.fieldKey}
                                                </span>
                                            </td>
                                            <td>{renderValue(req, 'old')}</td>
                                            <td>{renderValue(req, 'new')}</td>
                                            <td className={activeTab === 'pending' ? "is-right" : ""}>
                                                {activeTab === 'pending' ? (
                                                    <div className="tcvRowActions">
                                                        <button className="tcvActionBtn tcvActionBtn--ok" onClick={() => handleApprove(req)} title="Approuver">
                                                            <Check size={16} />
                                                        </button>
                                                        <button className="tcvActionBtn tcvActionBtn--no" onClick={() => handleReject(req)} title="Rejeter">
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div className="tcvPrimary">{req.validatedAt ? new Date(req.validatedAt).toLocaleDateString() : 'N/A'}</div>
                                                        {req.status === 'rejected' && (
                                                            <div className="tcvSub" style={{ color: '#ef4444' }} title={req.rejectionReason}>
                                                                Motif: {req.rejectionReason?.substring(0, 20)}...
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ValidationModifProfil;
